import { Address } from "./address.model";
import { OperationType } from "./organization.model";

export interface RelatedFinancialInstrument {
    id: string;
    instrumentId?: string;
    replacementApprovalDate?: Date;
}

export interface FinancialInstrument {
    id: string;
    amount: number;
    instrumentId?: string;
    isActive?: boolean,
    guarantor?: Guarantor;
    lastReviewedDate?: Date;
    lastReviewedDateReviewerUserId?: string;
    organizationId: string;
    typeCode: FinancialInstrumentType;
    modifiedDate?: Date;
    expirationDate?: Date;
    releasedDate?: Date;
    replacementApprovalDate?: Date;
    replacedBy?: RelatedFinancialInstrument;
    replacedInstrument?: RelatedFinancialInstrument;
}

export interface ExpiringFinancialInstrument{
    instrument: FinancialInstrument;
    organizationName: string;
}
export interface CertificateOfDeposit extends FinancialInstrument {
    term: number;
    issueDate: Date;
    maturityDate: Date;
}

export interface LetterOfCredit extends FinancialInstrument {
    effectiveDate: Date;
}

export interface FinancialStatement extends FinancialInstrument {
    ownerName: string;
    confirmedDateThatFirstRequestForUpdateWasReceived: Date;
    firstConfirmationUserId: string;
    confirmedDateThatSecondRequestForUpdateWasReceived: Date;
    secondConfirmationUserId: string;
}

export interface Guarantor {
    name: string;
    address?: Address;
    phoneNumber?: string;
    faxNumber?: string;
    email?: string;
}

export enum FinancialInstrumentType {
    Cash = 'C',
    CD = 'D',
    SuretyBond = 'B',
    LetterOfCredit = 'LC',
    FinancialStatement = 'F',
    DomesticCashFee = "DC",
    Aggregate = "Aggregate",
    MinimumAmountCombinedFeesAndAggregate = "CombinedFeeAndAggregate",
}

export interface Insurance {
    id: string;
    organizationId: string;
    agency?: string;
    agent?: string;
    emailAddress?: string;
    phoneNumber?: string;
    policies?: InsurancePolicy[]
}

export interface ExpiringInsurancePolicy {
    insuranceId: string;
    organizationId: string;
    organizationName: string;
    agency: string;
    agent?: string;
    emailAddress?: string;
    phoneNumber: string;
    policy: InsurancePolicy; 
}

export interface RelatedInsurancePolicy {
    insuranceId: string;
    policyNumber: string;
}

export interface InsurancePolicy {
    agencyId: string;
    agent?: string;
    limit: number;
    financialInstrumentId?: string;
    effectiveDate: Date;
    expirationDate: Date;
    releasedDate?: Date;
    hasEnvironmentalEndorsement: boolean;
    insurer?: string;
    isActive: boolean;
    policyNumber?: string;
    type?: string;
    surplusInsurer?: boolean;
    replacementApprovalDate?: Date;
    replacedBy?: RelatedInsurancePolicy[];
    replacedPolicy?: RelatedInsurancePolicy[];
}

export interface FinancialAssuranceSummary {
    bondingDecisionBasis: string;
    bondingProvided: number;
    bondingRequired: number;
    complianceStatus: string;
    insuranceDecisionBasis: string;
    insuranceProvided: number;
    insuranceRequired: number;
    isBrineHauler: boolean;
    isWellOwner: boolean;
    isInComplianceForBonding: boolean;
    isInComplianceForInsurance: boolean;
    hasExistingBondForfeitureChiefsOrder: boolean;
    hasEnvironmentalInsuranceForHorizontalShale: boolean;
    historicalOrganizationId: string;
    historicalOrganizationName: string;
    organizationId: string;
    organizationName: string;
    requiredCumulativeFinancialInstruments?: FinancialInstrument[];
    requiredDomesticWellFinancialInstruments?: FinancialInstrument[];
    requiredNonDomesticWellFinancialInstruments?: FinancialInstrument[];
}

export interface ExpiringInsuranceDetailed extends ExpiringInsurancePolicy {
    expirationDate?: Date;
    fortyFiveDayDayReminderSent?: Date;
    fortyFiveDayDayReminderReceived?: Date;
    legacyOrganizationId: string;
    type: string;
    number: string;
    ownerType?: OperationType;
}

export interface RequiredCumulativeFinancialInstrument {
    amount: number;
    typeCode: string;
}

export interface StratigraphicWellFinancialAssuranceView {
    apiNumber: string;
    companyName: string;
    organizationId: string;
    rbdmsOwnerNumber?: string;
    ownerType: string;
    spudDate?: Date;
    inCompliance: boolean;
    bondingRequired: number;
    bondingProvided: number;
    bondType?: string;
    bondingRequiredBy?: Date;
    bondExpirationDate?: Date;
    rows: StratigraphicWellFinancialAssuranceRow[];
}

export interface StratigraphicWellFinancialAssuranceRow {
    apiNumber: string;
    companyName: string;
    organizationId: string;
    rbdmsOwnerNumber?: string;
    ownerType: string;
    spudDate?: Date;
    bondingRequired?: number;
    bondingRequiredBy?: Date;
    inCompliance: boolean;
    instrumentTypeCode?: string;
    instrumentTypeDescription?: string;
    instrumentAmount?: number;
    instrumentEffectiveDate?: Date;
    instrumentExpirationDate?: Date;
}

export interface BondingReportModel {
    applicationNumber: string;
    instrumentType: string;
    dateReceived?: Date;
    amount: number;
    instrumentId: string;
    ownerName: string;
    ricsOrgId: string;
    rbdmsOwnerNumber: string;
    wellApiNumber: string;
}